import { FormShape, FormUnit, type GetFormInput, type GetFormOutputVerbose } from "@suse-o11y/signal/form";
import { z } from "zod";

import { isStringDirty } from "~/tools/is-string-dirty";
import { Template } from "~/tools/template";

import type { DashboardWidgetLink } from "../../../common/dashboard-spec";

type FormState = FormShape<{
	url: FormUnit<string, ReadonlyArray<string>, Template<["${", "}"], null>>;
	name: FormUnit<string, null, undefined | Template<["${", "}"], null>>;
	tooltip: FormUnit<string, null, undefined | Template<["${", "}"], null>>;
	targetBlank: FormUnit<boolean>;
}>;

type Input = GetFormInput<FormState>;

const INITIAL: Input = {
	url: "",
	name: "",
	tooltip: "",
	targetBlank: false,
};

function toInput({ name, url, tooltip, targetBlank }: DashboardWidgetLink): Input {
	return {
		name: name?.source ?? INITIAL.name,
		url: url.source ?? INITIAL.url,
		tooltip: tooltip?.source ?? INITIAL.tooltip,
		targetBlank: targetBlank ?? INITIAL.targetBlank,
	};
}

function init(initial: Input = INITIAL): FormState {
	return FormShape({
		url: FormUnit(initial.url, {
			schema: z
				.string()
				.trim()
				.min(1, "URL cannot be empty")
				.transform((url) => Template<null>(url)),
			isInputDirty: isStringDirty,
		}),

		name: FormUnit(initial.name, {
			transform: (input) => {
				const trimmed = input.trim();

				return trimmed ? Template<null>(trimmed) : undefined;
			},
			isInputDirty: isStringDirty,
		}),

		tooltip: FormUnit(initial.tooltip, {
			isInputDirty: isStringDirty,
			transform: (input) => {
				const trimmed = input.trim();

				return trimmed ? Template<null>(trimmed) : undefined;
			},
		}),

		targetBlank: FormUnit(initial.targetBlank),
	});
}

function toSpec({ name, url, tooltip, targetBlank }: GetFormOutputVerbose<FormState>): DashboardWidgetLink {
	return {
		url: url ?? Template(""),
		name: name ?? undefined,
		tooltip: tooltip ?? undefined,
		targetBlank: targetBlank ?? undefined,
	};
}

const FormState = {
	initial: INITIAL,
	toInput,
	init,
	toSpec,
};

export { FormState };
